import { Tabs } from "@heroui/react";
import { Key, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { AppSettingsPanel } from "@/components/app-settings/app-settings-panel";
import { BannersPanel } from "@/components/banners/banners-panel";
import { AppPage } from "@/layouts/page";

const TABS = ["banners", "settings"] as const;

type StorefrontTab = (typeof TABS)[number];

const isStorefrontTab = (value: string | null): value is StorefrontTab =>
  TABS.some((tab) => tab === value);

export default function StorefrontPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // The header's action area belongs to whichever tab is open: each panel draws its own buttons into it.
  const [actionSlot, setActionSlot] = useState<HTMLElement | null>(null);
  const tabParam = searchParams.get("tab");
  const selectedTab: StorefrontTab = isStorefrontTab(tabParam)
    ? tabParam
    : "banners";

  const onSelectionChange = (key: Key) =>
    setSearchParams({ tab: String(key) }, { replace: true });

  return (
    <AppPage
      action={<div ref={setActionSlot} className="flex items-center gap-3" />}
      subtitle="Banners, notices and order pricing for the mobile app and website"
      title="Storefront"
    >
      <Tabs
        className="flex min-h-0 flex-1 flex-col"
        selectedKey={selectedTab}
        align="start"
        variant="secondary"
        onSelectionChange={onSelectionChange}
      >
        <Tabs.ListContainer>
          <Tabs.List aria-label="Storefront sections">
            <Tabs.Tab className="w-auto" id="banners">
              Banners
              <Tabs.Indicator />
            </Tabs.Tab>
            <Tabs.Tab className="w-auto" id="settings">
              Settings
              <Tabs.Indicator />
            </Tabs.Tab>
          </Tabs.List>
        </Tabs.ListContainer>
        <Tabs.Panel
          className="flex min-h-0 flex-1 flex-col p-0 pt-4"
          id="banners"
        >
          <BannersPanel actionSlot={actionSlot} />
        </Tabs.Panel>
        <Tabs.Panel
          className="flex min-h-0 flex-1 flex-col overflow-y-auto p-0 pt-4"
          id="settings"
        >
          <AppSettingsPanel actionSlot={actionSlot} />
        </Tabs.Panel>
      </Tabs>
    </AppPage>
  );
}
